import * as amplitude from "@amplitude/analytics-browser";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import { useSupportHelpdeskV2Enabled } from "@redotech/redo-merchant-app-common/setting/use-support-helpdesk-v2-enabled";
import { useSupportTicketQueryScope } from "@redotech/redo-merchant-app-common/support/use-support-ticket-query-scope";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { showNotification } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useContext, useEffect } from "react";
import { useLocalStorage } from "usehooks-ts";
import { talentPopAmplitudeAttributes } from "./analytics";
import { CALENDLY_LINK, LEARN_MORE_LINK } from "./links";
import {
  getTalentPopNotificationCopy,
  type TalentPopTrigger,
} from "./notification-copy";
import { useTalentPopTrigger } from "./use-trigger";

/**
 * Per-team localStorage key holding the last dismissed trigger cycle.
 * Per-team so dismissing on one team doesn't suppress the toast on another.
 */
function notificationDismissedKey(teamId: string | null | undefined): string {
  return `redo.talentpop-notification-dismissed.${teamId ?? "anon"}`;
}

/**
 * Stable dismissal key for a trigger. Picked so that:
 *
 *  - dismissing once during a given holiday cycle suppresses the
 *    notification until the next year's run-up opens (the key includes
 *    the calendar year),
 *  - dismissing during a volume surge suppresses it for the rest of that
 *    ISO week so the toast doesn't re-pop after a refresh.
 */
function triggerDismissalKey(
  trigger: TalentPopTrigger,
  today: Temporal.PlainDate,
): string {
  if (trigger.type === "holiday") {
    return `holiday:${trigger.holidayKey}:${today.year}`;
  }
  // Key on the ISO week's Monday so the suppression window is exactly one
  // week and unambiguous across the Dec/Jan boundary — `${year}-W${weekOfYear}`
  // would be wrong for ~1 week/year because the ISO week-year diverges from
  // the calendar year there.
  const isoWeekStart = today.subtract({ days: today.dayOfWeek - 1 });
  return `volume:${isoWeekStart.toString()}`;
}

/**
 * Mounts once near the app root and fires the TalentPop notification when
 * the holiday/volume trigger transitions to active, suppressed once
 * dismissed for the current trigger cycle. Renders nothing.
 *
 * The outer `TalentPopNotificationHost` guards on a loaded team: this inner
 * worker calls `useSupportHelpdeskV2Enabled`, which reads `TeamContext` via
 * `useRequiredContext` and throws when no team is present. The host mounts at
 * the app root, which also renders on team-less routes (login, token login,
 * the pre-team load window), so the worker must only run once a team exists.
 */
const TalentPopNotificationHostInner = memo(
  function TalentPopNotificationHostInner() {
    const trigger = useTalentPopTrigger();
    const queryScope = useSupportTicketQueryScope();
    const { enabled: supportEnabled } = useSupportHelpdeskV2Enabled();
    const [dismissedKey, setDismissedKey] = useLocalStorage<string | null>(
      notificationDismissedKey(queryScope.teamId),
      null,
    );

    useEffect(() => {
      if (trigger === null) {
        return;
      }
      // Only surface the TalentPop upsell to merchants on support v2. The
      // holiday trigger is purely date-based, so without this gate the toast
      // would fire for every merchant during a holiday run-up regardless of
      // whether they use Redo Support.
      if (!supportEnabled) {
        return;
      }
      // Wait for a resolved team before firing. The holiday trigger is purely
      // date-based, so on a cold load (teamId still null) it would enqueue an
      // "anon"-keyed toast and then enqueue a second one once the team
      // resolves and the localStorage/enqueue keys change.
      if (queryScope.teamId === null) {
        return;
      }
      const key = triggerDismissalKey(trigger, Temporal.Now.plainDateISO());
      if (dismissedKey === key) {
        return;
      }
      // Stable per-(team, cycle) key so the provider won't stack a second
      // toast while this one is still visible (e.g. effect re-runs on
      // unrelated dependency churn or a team switch and back).
      const notificationKey = `talentpop:${queryScope.teamId}:${key}`;
      const copy = getTalentPopNotificationCopy(trigger);
      const attributes = talentPopAmplitudeAttributes(
        "notification",
        trigger,
        queryScope.teamId,
      );
      amplitude.logEvent("talentpop-notification-shown", attributes);
      showNotification({
        notificationKey,
        title: copy.title,
        description: copy.description,
        featuredIcon: { Icon: AlertTriangleIcon, color: "warning" },
        onDismiss: () => {
          amplitude.logEvent("talentpop-notification-dismissed", attributes);
          setDismissedKey(key);
        },
        children: (dismiss) => (
          <Flex gap="sm">
            <RedoButton
              hierarchy="secondary"
              onClick={() => {
                amplitude.logEvent("talentpop-cta-clicked", {
                  ...attributes,
                  cta: "learn-more",
                });
                window.open(LEARN_MORE_LINK, "_blank", "noopener");
                setDismissedKey(key);
                dismiss();
              }}
              size="sm"
              text="Learn more"
            />
            <RedoButton
              hierarchy="primary"
              onClick={() => {
                amplitude.logEvent("talentpop-cta-clicked", {
                  ...attributes,
                  cta: "book-a-call",
                });
                window.open(CALENDLY_LINK, "_blank", "noopener");
                setDismissedKey(key);
                dismiss();
              }}
              size="sm"
              text="Book a call"
            />
          </Flex>
        ),
      });
    }, [
      trigger,
      dismissedKey,
      setDismissedKey,
      queryScope.teamId,
      supportEnabled,
    ]);

    return null;
  },
);

/**
 * Team-presence guard for {@link TalentPopNotificationHostInner}. Reads the
 * team via a non-throwing `useContext` and renders nothing until one is loaded,
 * so the worker's `useRequiredContext(TeamContext)` never fires on the app
 * root's team-less renders (login, token login, the pre-team load window).
 */
export const TalentPopNotificationHost = memo(
  function TalentPopNotificationHost() {
    const team = useContext(TeamContext);
    if (!team) {
      return null;
    }
    return <TalentPopNotificationHostInner />;
  },
);
